import type { NormalizedServiceReport, ServiceReport } from "@entity/ServiceReport";
import { linkifyMessage } from "./linkifyMessage";

/**
 * Copies an alert and its updates, making plain URLs clickable without changing the input.
 */
export function formatUrl<T extends { statusUpdates?: Array<{ message: string }> | null }>(
  alert: T,
): T & { statusUpdates: Array<{ message: string }> } {
  // Vue 2 called formatUrl from the incidents getter. A missing, null, or
  // non-array statusUpdates threw TypeError on .map during render: no redirect,
  // H.error was not called, and the whole incidents list failed to draw.
  // formatUrl now runs inside setReport/normalizeReport. The same throw would
  // look like a failed fetch (replace("/") + H.error). Keep the load successful,
  // publish this alert with [] so getters and IncidentDescription do not crash,
  // and put the explicit error in the console instead of a render TypeError.
  if (!Array.isArray(alert.statusUpdates)) {
    console.error(
      "Can't read status dashboard report: alert statusUpdates must be an array",
      alert,
    );

    return {
      ...alert,
      statusUpdates: [],
    };
  }

  return {
    ...alert,
    statusUpdates: alert.statusUpdates
      .filter((update, index) => {
        if (update && typeof update === "object" && typeof update.message === "string") {
          return true;
        }

        console.error(
          "Can't read status dashboard report: skipping invalid status update",
          { index, update },
        );
        return false;
      })
      .map((update) => ({
        ...update,
        message: linkifyMessage(update.message),
      })),
  };
}

/**
 * Prepares every row, alert and update before the store publishes a report.
 * Alert arrays become maps with their original index keys, preserving incident IDs and order.
 * Copies all transformed containers so frozen API responses can be reused safely.
 */
export function normalizeReport(report: ServiceReport): NormalizedServiceReport {
  // Vue 2 used includes/toLowerCase without type checks, so malformed labels
  // could crash rendering. Now skip only that row before grouping and t(),
  // keeping valid rows and logging the API contract violation.
  const validRows = report.filter((item, index) => {
    if (typeof item?.service === "string" && typeof item?.region === "string") {
      return true;
    }

    console.error(
      "Can't read status dashboard report: skipping row with non-string service or region",
      { index, item },
    );
    return false;
  });

  return validRows.map((item) => ({
    ...item,
    alerts: Object.fromEntries(
      Object.entries(item.alerts ?? {})
        .filter(([id, alert]) => {
          if (alert && typeof alert === "object" && !Array.isArray(alert)) {
            return true;
          }

          console.error(
            "Can't read status dashboard report: skipping non-object alert",
            { id, alert },
          );
          return false;
        })
        .map(([id, alert]) => [id, formatUrl(alert)]),
    ),
  }));
}
